import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import eventBus from "../../utils/eventBus";
import Remote from "../../components/remote/remote";
import { useAuth } from "../../components/context/authContext";
import { useNotifications } from "../../components/context/notificationContext";
import { isSuspendedFrom } from "../../utils/membersSiteAccessPermissions";
import {
  apiGetListFromSourceID,
  apiRecordUserSkitNotInterested,
  apiFetchUserSkitNotInterested,
  apiRecordUserSusbcribeActivity,
  apiUnSusbcribeUser,
  apiFetchUserSubscriptionStatus,
} from "../../api/backend/user";
import {
  apiRewardSkitSubscriberPromo,
  apiDeleteSkitIdFromSourceIdAction,
  apiReportSkit,
} from "../../api/backend/skits";
import { useUserSourceDetails } from "../../components/hooks/useUserDetails";
import { useCurrentUserAcctDetails } from "../../components/hooks/useUserDetailsProfileClick";
import { fetchUserDetails } from "../../controller/profileUpdateController";
import { renderUserThumbnail } from "../../controller/userThumbnail";
import Loader from "../../components/loader/loader";
import ClapperBoard from "../../components/clapperBoard/animatedClapperBoard";
import {
  SmallPlainInsetBtn,
  GlassBtn,
  StripeAnimationBtn,
} from "../../components/buttons";
import {
  MegaphoneIcon,
  PencilIcon,
  BellIcon,
  DownArrowIcon,
  NoBellIcon,
  EyeIcon,
  LineChartIcon,
  LineChartReportIcon,
} from "../../components/icons";
import SkitAdCard from "../../components/skitAd/skitAdCard";
import AcctHolderUpdateModal from "../../components/forms/acctHolderUpdateModal";
import Tv from "../../components/tv/tv";
import RoleBasedButtons from "../../components/roleBasedButtons/roleBasedButtons";
import ReportModal from "../../components/reportModal/reportModal";
import { SingularPlural } from "../../utils/wordUtils";
import Television from "../../components/television/television";
import { FORM_FLOW_CONTROL } from "../../constants/currentContextTypes";
import "react-phone-input-2/lib/material.css";
import "./userProfilePage.scss";

const UserProfilePage = () => {
  const {
    currentMemberId,
    openSkitUploadForm,
    currentMemberData,
    setCurrentMemberData,
    verifyNeeded,
    setVerifyNeeded,
    setCurrentContextType,
    setCurrentFormStep,
    handleLogout,
    countRefreshLoggedinAcctHldrProfileLoad,
    notInterested,
    setNotInterested,
    setSuccessFlashMessage,
  } = useAuth();
  const navigate = useNavigate();
  const {
    sourceUserAcctDetails,
    getSourceAccountHolderDetailsById, // get the account details from the user id and will later compare with current user id to see who is the account holder for permissions
  } = useUserSourceDetails();
  const { notificationsCountFetchedFromMiddleware } = useNotifications();
  const { fetchDetailsOnProfileClick } = useCurrentUserAcctDetails(); // this does not need to be compared for account holder becaause the current user is the account holder for clicking on the profile button
  const [accountHolder, setAccountHolder] = useState(false);
  const { userId } = useParams(); // This user id comes from the slider button that is passed, user can be logged in or not logged in
  const [editMode, setEditMode] = useState(false);
  const [reportingSkit, setReportingSkit] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isClapping, setIsClapping] = useState(false);
  const [skitsData, setSkitsData] = useState([]);
  const [colorPaletteVisible, setColorPaletteVisible] = useState(false);
  const [sticky, setSticky] = useState(false);
  const [noSkitsAvailable, setNoSkitsAvailable] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [thumbnailPreference, setThumbnailPreference] = useState(null);
  const [currentEmotionId, setCurrentEmotionId] = useState(null);
  const onLogoutClick = () => {
    handleLogout();
  };
  const fetchSubscriptionStatus = async () => {
    if (!currentMemberId || !userId) return; // Ensure both IDs are present
    try {
      const response = await apiFetchUserSubscriptionStatus({
        loggedInAccountHolderId: currentMemberId,
        targetId: userId,
      });
      const data = response.data; // Correctly extract data from the response object
      // count-only: if backend included an explicit counter, hydrate override
      if (data?.success) {
        // keep the UI state for the subscribe button in sync
        setSubscriptionStatus(data.isSubscribed ? "subscribed" : "subscribe");
        // hydrate badge if server sent a count (optional field)
        if (typeof data.unreadNotificationCount === "number") {
          notificationsCountFetchedFromMiddleware(data.unreadNotificationCount); // -> pushDbSnapshotValue
        }
      } else {
        console.error("Failed to fetch subscription status: ", data?.message);
        setError("Failed to load subscription status");
      }
    } catch (error) {
      console.error("Error fetching subscription status: ", error);
      setError("Failed to load subscription status");
      // Handle the case where the server does not respond or the request is malformed
    }
  };

  useEffect(() => {
    // sets the data from the backend response on mount
    const run = async () => {
      if (!currentMemberId || !userId) {
        setSubscriptionStatus(null);
        return;
      }
      // ✅ Guard: if the logged-in account is viewing their own profile,
      //    subscription status is not applicable (no subscribe button context).
      //    Keep it null to avoid rendering subscribe/unsubscribe incorrectly.
      if (String(currentMemberId) === String(userId)) {
        setSubscriptionStatus(null);
        return;
      }
      // ✅ Reset to unknown before fetching.
      //    This prevents stale subscriptionStatus from a previous profile from flashing
      //    while we fetch the real backend truth for the new target profile.
      setSubscriptionStatus(null);
      // ✅ Fetch backend truth for this (currentMemberId -> userId) relationship.
      //    fetchSubscriptionStatus is responsible for setting:
      //      "subscribed" or "subscribe" based on data.isSubscribed from the server,
      //    and for hydrating unreadNotificationCount if returned.
      await fetchSubscriptionStatus();
    };
    // ✅ Execute the async workflow.
    //    (We wrap it in a function because useEffect callbacks cannot be async.)
    run();
    // ✅ Re-run whenever:
    //    - currentMemberId changes (auth hydrates / account switches / relogin)
    //    - userId changes (navigating between profiles)
  }, [currentMemberId, userId]);


  
  const handleSubscribeClick = async () => {
    // Guard: we need both ids to proceed
    if (!currentMemberId || !userId) {
      console.warn("[Subscribe] missing ids", { currentMemberId, userId });
      return; // stop early
    }
    // The profile owner we’re looking at (target/creator)
    const targetId = userId;
    // The logged-in member performing the action (actor/source)
    const loggedInAccountHolderId = currentMemberId;
    console.group("[Subscribe] flow");
    console.log("[Subscribe] start", {
      targetId,
      loggedInAccountHolderId,
      subscriptionStatus,
    });
    setLoading(true); // lock UI while we work
    try {
      // If we’re not in a state that allows subscribing, just open the UI and bail
      if (subscriptionStatus !== "subscribe") {
        console.log("[Subscribe] non-subscribe status, opening dropdown", {
          subscriptionStatus,
        });
        return; // nothing else to do here
      }
      // --- call #1: record the subscription activity on the server ---
      console.time("[Subscribe] apiRecordUserSusbcribeActivity");
      let subscriptionResponse;
      try {
        subscriptionResponse = await apiRecordUserSusbcribeActivity({
          targetId, // who I’m subscribing to
          loggedInAccountHolderId, // who is subscribing
        });
        console.log("[Subscribe] record:ok", {
          status: subscriptionResponse?.status,
          success: subscriptionResponse?.data?.success,
          message: subscriptionResponse?.data?.message,
        });
      } catch (e) {
        // Log shape + rethrow so outer catch handles UX
        console.error("[Subscribe] record:err", {
          status: e?.response?.status,
          data: e?.response?.data,
          message: e?.message,
        });
        throw e;
      } finally {
        console.timeEnd("[Subscribe] apiRecordUserSusbcribeActivity");
      }
      // Extract any notifications returned in this response (helper flattens/normalizes)
      // Count-only: if API returns unreadNotificationCount, hydrate override
      const recCount = subscriptionResponse?.data?.unreadNotificationCount;
      if (typeof recCount === "number") {
        notificationsCountFetchedFromMiddleware(recCount); // -> pushDbSnapshotValue
      }
      // If the server said “no” (e.g., validation), stop here
      if (!subscriptionResponse?.data?.success) {
        console.error("[Subscribe] record:failed", subscriptionResponse?.data);
        return;
      }
      // --- call #2: run the subscriber promo (may bump a skit, etc.) ---
      console.time("[Subscribe] apiRewardSkitSubscriberPromo");
      let promoResponse;
      try {
        promoResponse = await apiRewardSkitSubscriberPromo({
          loggedInAccountHolderId, // actor/source
          targetId, // creator/target
        });
        console.log("[Subscribe] promo:ok", {
          status: promoResponse?.status,
          hasSkit: !!promoResponse?.data?.skit,
        });
      } catch (e) {
        console.error("[Subscribe] promo:err", {
          status: e?.response?.status,
          data: e?.response?.data,
          message: e?.message,
        });
        throw e;
      } finally {
        console.timeEnd("[Subscribe] apiRewardSkitSubscriberPromo");
      }
      // Extract notifications from the promo call
      const promoCount = promoResponse?.data?.unreadNotificationCount;
      if (typeof promoCount === "number") {
        notificationsCountFetchedFromMiddleware(promoCount); // -> pushDbSnapshotValue
      }
      // Update UI state to reflect success
      setSubscriptionStatus("subscribed");
      console.log("[Subscribe] setSubscriptionStatus -> subscribed");
      // Keep the dropdown open to show post-subscribe actions
      // Refresh any profile data that might have changed
      fetchUserDetailsController();
      console.log("[Subscribe] fetchUserDetailsController fired");
      // If a skit was bumped/promoted, broadcast it to listeners
      if (promoResponse?.data?.skit) {
        console.log("[Subscribe] emitting skitPromoted event", {
          skitId: promoResponse.data.skit?.id,
        });
        eventBus.emit("skitPromoted", null, promoResponse.data.skit);
      }
    } catch (error) {
      // If the backend indicates the account is suspended, log out gracefully
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        console.warn("[Subscribe] suspended — logging out");
        await handleLogout();
        console.groupEnd();
        return;
      }
      // Generic error logging
      console.error("[Subscribe] flow error", {
        msg: error?.message,
        status: error?.response?.status,
        data: error?.response?.data,
      });
      // Try to surface any error notifications
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware(errCount);
      }
    } finally {
      setLoading(false); // unlock UI
      console.log("[Subscribe] loading -> false");
      console.groupEnd(); // close console group
    }
  };

  const handleUnsubscribeClick = async () => {
    if (!currentMemberId || !userId) return;
    const loggedInAccountHolderId = currentMemberId;
    const targetId = userId;
    setLoading(true);
    try {
      const res = await apiUnSusbcribeUser({
        loggedInAccountHolderId, // // Subscriber who will unsubscribe
        activity: "unsubscribed",
        targetId,
      });
      if (isSuspendedFrom(res) || isSuspendedFrom(res?.data)) {
        await handleLogout();
        return;
      }
      // Count-only: hydrate if backend sends count
      const count = res?.data?.unreadNotificationCount;
      if (typeof count === "number") {
        notificationsCountFetchedFromMiddleware(count);
      }
      if (res?.data?.success) {
        setSubscriptionStatus("subscribe");
        fetchUserDetailsController();
      } else {
        console.error("Failed to unsubscribe:", res?.data?.message);
      }
    } catch (error) {
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout();
        return;
      }
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware(errCount);
      }
      console.error("Error unsubscribing:", error);
    } finally {
      setLoading(false);
    }
  };

  // this function will prevent the skit(s) from displaying to the current user since it is recorded as not interested in the user's activity log
  const fetchCurrentUserNotInterestedActivityLogs = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiFetchUserSkitNotInterested({
        loggedInAccountHolderId: currentMemberId, // was sourceId
      });
      // eject if server signals suspension
      if (isSuspendedFrom(response) || isSuspendedFrom(response?.data)) {
        await handleLogout();
        return;
      }
      // 🔔 hydrate unread badge
      const count =
        typeof response?.data?.unreadNotificationCount === "number"
          ? response.data.unreadNotificationCount
          : typeof response?.unreadNotificationCount === "number"
            ? response.unreadNotificationCount
            : null;
      if (typeof count === "number") {
        notificationsCountFetchedFromMiddleware(count);
      }
      // tolerant parse + filter
      const body = response?.data !== undefined ? response.data : response;
      const list = Array.isArray(body?.data)
        ? body.data
        : Array.isArray(body)
          ? body
          : [];
      setNotInterested(list.map((log) => log.relatedId));
    } catch (error) {
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout();
        return;
      }
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware(errCount);
      }
      console.error("Failed to fetch 'not interested' logs:", error);
      setError("Failed to load 'not interested' logs");
    } finally {
      setLoading(false);
    }
  }, [currentMemberId, notificationsCountFetchedFromMiddleware, handleLogout]);

  // allows the user to record not interested in the database from a button
  const handleNotInterested = useCallback(
    async (skit) => {
      try {
        const response = await apiRecordUserSkitNotInterested({
          loggedInAccountHolderId: currentMemberId,
          activity: "notInterested",
          relatedId: skit.id,
          targetId: skit.sourceId,
        });
        console.log("Response from not interested API:", response);
        // suspension eject
        if (isSuspendedFrom(response) || isSuspendedFrom(response?.data)) {
          await handleLogout();
          return;
        }
        // 🔔 hydrate badge if backend included a count
        const count = response?.data?.unreadNotificationCount;
        if (typeof count === "number") {
          notificationsCountFetchedFromMiddleware(count);
        }
        if (response?.data && response.data.success) {
          console.log("Current notInterested before update:", notInterested);
          setNotInterested((prev) => [...prev, skit.id]); // Adds the skit ID
          console.log(
            "Skit is now marked as not interested and should be hidden:",
            skit.id
          );
        }
      } catch (error) {
        // suspension on error path too
        if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
          await handleLogout();
          return;
        }
        // 🔔 hydrate badge from error payload if present
        const errCount =
          error?.response?.data?.unreadNotificationCount ??
          error?.unreadNotificationCount ??
          null;
        if (typeof errCount === "number") {
          notificationsCountFetchedFromMiddleware(errCount);
        }

        console.error("Error marking skit as not interested:", error);
        setError("Failed to mark skit as not interested");
      }
    },
    [
      currentMemberId,
      notificationsCountFetchedFromMiddleware,
      handleLogout,
      notInterested,
    ]
  );

  const fetchUserDetailsController = async () => {
    await fetchUserDetails({
      currentMemberId,
      userId,
      setCurrentMemberData,
      setAccountHolder,
      setThumbnailPreference,
      setCurrentEmotionId,
      setLoading,
      fetchDetailsOnProfileClick,
      getSourceAccountHolderDetailsById,
      setError,
      setSkitsData,
      setNoSkitsAvailable,
      handleLogout,
      notificationsCountFetchedFromMiddleware,
    });
  };
  // fetch user details on mount or when userId/currentMemberId changes
  useEffect(() => {
    fetchUserDetailsController();
  }, [
    userId,
    currentMemberId,
    fetchDetailsOnProfileClick,
    getSourceAccountHolderDetailsById,
    countRefreshLoggedinAcctHldrProfileLoad,
  ]);

  // Use the fetched data to decide which user's details to display
  // also target user when current user is subscribing or unsubscribing so in this context current user is source id for subscribed activity log
  useEffect(() => {
    // If viewing another user's profile and their details are loaded
    // user id is not equal to current user
    if (userId !== currentMemberId && sourceUserAcctDetails) {
      setCurrentMemberData(sourceUserAcctDetails);
      setThumbnailPreference(sourceUserAcctDetails.thumbnailPreference);
      setCurrentEmotionId(sourceUserAcctDetails.emotionconId);
      setAccountHolder(false);
    }
  }, [userId, currentMemberId, sourceUserAcctDetails]);

  const scrollWithOffset = (el, offset) => {
    const elementPosition = el.getBoundingClientRect().top;
    const scrollY = window.scrollY;
    const offsetPosition = elementPosition + scrollY - offset;
    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  };

  const handleClap = async () => {
    setIsClapping(true);
    setLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 500));
      const response = await apiGetListFromSourceID(currentMemberData.id);
      if (response.data && Array.isArray(response.data.skits)) {
        setSkitsData(response.data.skits); // Use response.data.skits, not response.data
        setNoSkitsAvailable(response.data.skits.length === 0);
      } else {
        console.error("Unexpected response structure:", response);
        setNoSkitsAvailable(true);
      }
    } catch (error) {
      console.error("Failed to fetch skits:", error);
      setSkitsData([]); // Fallback to an empty array
    } finally {
      setIsClapping(false);
      setLoading(false);
    }
  };

  const handleDeleteSourceIdRelatedIdSkit = (e, skitId) => {
    console.log("Attempting to delete skit with ID:", skitId); // Log skitId

    apiDeleteSkitIdFromSourceIdAction({ skitId })
      .then((response) => {
        if (response.status === 200) {
          console.log("Skit deleted successfully:", skitId); // Log success message
          // Assuming a successful delete operation
          const updatedSkitsData = skitsData.filter(
            (skit) => skit.id !== skitId
          );
          setSkitsData(updatedSkitsData);
        } else {
          console.error("Failed to delete skit:", response); // Log failure response
          alert("Failed to delete the skit");
        }
      })
      .catch((error) => {
        console.error("Error deleting skit:", error); // Log error details
        alert("An error occurred while deleting the skit");
      });
  };

  // Use useEffect to call the function on mount or when currentMemberId changes
  useEffect(() => {
    fetchCurrentUserNotInterestedActivityLogs();
  }, [fetchCurrentUserNotInterestedActivityLogs]);

  // console.log(currentMemberData); // Right before the return statement in the Skits component

  const toggleColorPaletteVisibility = () => {
    setColorPaletteVisible(!colorPaletteVisible);
  };
  const handleScroll = () => {
    const scrollTop = window.scrollY;
    setSticky(scrollTop > 100); // You might adjust the scroll threshold as needed
  };
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (String(userId) === String(currentMemberId)) {
      setAccountHolder(true);
    }
  }, [userId, currentMemberId]);

  const reportSkit = async (choices) => {
    // minimal inline flash so you can keep it local
    const flash = (msg, ms = 1800) => {
      setSuccessFlashMessage?.(msg);
      clearTimeout(reportSkit._t);
      reportSkit._t = setTimeout(() => setSuccessFlashMessage?.(null), ms);
    };
    try {
      const res = await apiReportSkit({
        loggedInAccountHolderId: currentMemberId, // ← keep nomenclature
        skitId: reportingSkit.id,
        reason: JSON.stringify(choices),
      });
      // eject if backend signals suspension
      if (isSuspendedFrom(res) || isSuspendedFrom(res?.data)) {
        await handleLogout?.();
        return;
      }
      // 🔔 hydrate unread count (body preferred, then header)
      const bodyCount = Number(
        res?.data?.unreadNotificationCount ?? res?.unreadNotificationCount
      );
      if (Number.isFinite(bodyCount))
        notificationsCountFetchedFromMiddleware?.(bodyCount);
      const headerCount = Number(res?.headers?.["x-unread-notifications"]);
      if (Number.isFinite(headerCount))
        notificationsCountFetchedFromMiddleware?.(headerCount);
      flash("Report sent ✅");
      // if backend suspended it, re-fetch “page 1” via your existing clap refresh
      const skitStatus = res?.data?.data?.skitStatus ?? res?.data?.skitStatus;
      if (skitStatus === "suspended") {
        handleClap(); // pulls fresh feed
      }
    } catch (error) {
      // suspension on error path too
      if (isSuspendedFrom(error) || isSuspendedFrom(error?.response)) {
        await handleLogout?.();
        return;
      }
      // 🔔 hydrate from error payload if present
      const errCount =
        error?.response?.data?.unreadNotificationCount ??
        error?.unreadNotificationCount ??
        null;
      if (typeof errCount === "number") {
        notificationsCountFetchedFromMiddleware?.(errCount);
      }
      flash("Could not submit report. Try again.", 2200);
    } finally {
      setReportingSkit(null);
    }
  };

  const onEditProfileClick = () => {
    setVerifyNeeded(true); // <-- just flips the boolean on
    setCurrentContextType(FORM_FLOW_CONTROL.MEMBER_VERIFICATION_NEEDED);
  };
  const onVerified = () => {
    setVerifyNeeded(false); // hide TV, then open the modal
    // if the layout needs a tick to unmount TV first, use a tiny timeout:
    // setTimeout(() => setEditMode(true), 0);
    setCurrentContextType(FORM_FLOW_CONTROL.SIGN_IN_FORM); // baseline
    setCurrentFormStep(FORM_FLOW_CONTROL.SIGN_IN_FORM);
    setEditMode(true); // opens AcctHolderUpdateModal
  };

  return (
    <div id="userProfilePage">
      <div
        className={`${colorPaletteVisible ? "glassTray" : "topRemoteUserProfile"} ${sticky ? "sticky" : "topRemoteUserProfile"}`}
      >
        <Remote
          showChannels={false}
          onStartSkitUploadFlowClick={(statePayload) =>
            navigate("/upload-skit", { state: statePayload })
          }
          toggleColorPaletteVisibility={toggleColorPaletteVisibility}
          colorPaletteVisible={colorPaletteVisible}
          setColorPaletteVisible={setColorPaletteVisible}
          onClick={onLogoutClick}
        />
      </div>
      {accountHolder && (
        <RoleBasedButtons currentMemberId={currentMemberData} />
      )}
      {currentMemberData && !accountHolder && (
        <div className="usernameAndSubscribeWrapper">
          <p className="username">{currentMemberData.username}</p>
          <div className="subscribeContainer">
            <StripeAnimationBtn
              stripeAnimationBtnImage={
                subscriptionStatus === "subscribed" ? (
                  <NoBellIcon
                    height="20px"
                    width="20px"
                    style={{ marginRight: "3px" }}
                  />
                ) : (
                  <BellIcon
                    height="20px"
                    width="20px"
                    style={{ marginRight: "3px" }}
                  />
                )
              }
              extraStyles={{
                width: "fit-content",
                color: "rgb(176, 58, 46)",
                textShadow: "black 0px 0px",
                fontSize: "1rem",
                lineHeight: "14px",
                fontWeight: "700",
                fontFamily: "Roboto, sans-serif",
                textDecoration: "none",
                border: "1.5px solid var(--color-gold)",
                borderRadius: "30px",
                whiteSpace: "nowrap",
                backgroundImage:
                  "linear-gradient(0deg, rgb(216, 217, 219) 0px, rgb(255, 255, 255) 80%, rgb(253, 253, 253))",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                height: "30px",
                padding: "5px",
              }}
              stripeAnimationBtnText={
                subscriptionStatus === "subscribed"
                  ? "Unsubscribe"
                  : "Subscribe"
              }
              icon={null} // ✅ arrow gone
              width="fit-content"
              onClick={
                subscriptionStatus === "subscribed"
                  ? handleUnsubscribeClick
                  : handleSubscribeClick
              }
            />
          </div>
        </div>
      )}
      {accountHolder && (
        <div className="glassBtnUserProfileDiv">
          <HashLink
            to={`/user-profile/${currentMemberId}/skit-dashboard#top`}
            scroll={(el) =>
              el.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            <GlassBtn
              glassBtnImage={
                <LineChartIcon
                  height="15px"
                  width="15px"
                  fillColor="var(--color-gold)"
                />
              }
              className="glass-btn"
              glassRefClassName="userProfileGlassBtnRef"
              glassBtnText="Skit Dashboard"
              glassBtnWidth="150px"
              glassBtnTextFontSize="1rem"
              glassBtnFontWeight="900"
              glassBtnHeight="20px"
              glassBtnFontColor="var(--color-gold)"
              glassBtnTextDecorationColor="var(--color-gold)"
              hi2Style={{
                transform: "rotate(90deg) translate(-60px, 75px)",
              }}
              hi3Style={{
                transform: "rotate(90deg) translate(-60px, -75px)",
              }}
            />
          </HashLink>
          <HashLink
            to={`/user-profile/${currentMemberId}/ads-dashboard#adsManagerTop`}
            scroll={(el) =>
              el.scrollIntoView({ behavior: "smooth", block: "start" })
            }
          >
            <GlassBtn
              glassBtnImage={
                <LineChartReportIcon
                  height="15px"
                  width="15px"
                  fillColor="var(--color-yellow)"
                />
              }
              className="glass-btn"
              glassRefClassName="adsManagerGlassBtnRef"
              glassBtnText="Ads Manager"
              glassBtnWidth="150px"
              glassBtnHeight="20px"
              glassBtnTextFontSize="1rem"
              glassBtnFontWeight="900"
              glassBtnFontColor="var(--color-yellow)"
              glassBtnTextDecorationColor="var(--color-yellow)"
              hi2Style={{
                transform: "rotate(90deg) translate(-60px, 75px)",
              }}
              hi3Style={{
                transform: "rotate(90deg) translate(-60px, -75px)",
              }}
            />
          </HashLink>
        </div>
      )}
      {accountHolder && (
        <div className="acctHldrProfileControlWrapper">
          <HashLink
            to={`/user/subscribers#subscriptionsTop`}
            scroll={(el) => scrollWithOffset(el, 50)}
            style={{
              textDecoration: "none",
              color: "var(--color-gold)", // Your chosen styles
            }}
          >
            <SmallPlainInsetBtn
              image={
                <EyeIcon
                  height="15px"
                  width="15px"
                  fillColor="var(--color-bloodred)"
                  style={{ marginRight: "3px" }}
                />
              }
              text="View Subscriptions"
              width="fit-content"
              style={{
                fontSize: "1rem",
              }}
            />
          </HashLink>
          <SmallPlainInsetBtn
            image={
              <PencilIcon
                height="15px"
                width="15px"
                style={{ marginRight: "3px" }}
              />
            }
            text="Edit Profile"
            width="fit-content"
            style={{
              fontSize: "1rem",
            }}
            onClick={onEditProfileClick}
          />
        </div>
      )}
      {currentMemberData && (
        <h2 className="username">{currentMemberData.userNameValue}</h2>
      )}
      {currentMemberData && (
        <>
          <div className="tv-wrapper">
            <div className="subscribersListLink">
              <Tv thumbnail={renderUserThumbnail(currentMemberData)} />
            </div>
            <div className="Action">
              <ClapperBoard isClapping={isClapping} />
              <SmallPlainInsetBtn
                image={
                  <MegaphoneIcon
                    height="15px"
                    width="15px"
                    style={{ marginRight: "3px" }}
                  />
                }
                text="Action"
                width="fit-content"
                style={{
                  fontSize: "1rem",
                }}
                onClick={handleClap}
              />
            </div>
          </div>
          <HashLink
            to={`/user/${userId}/subscribers#subscribersTop`}
            scroll={(el) => scrollWithOffset(el, 50)}
            style={{
              textDecoration: "none",
            }}
          >
            <SmallPlainInsetBtn
              image={
                <EyeIcon
                  height="15px"
                  width="15px"
                  fillColor="var(--color-bloodred)"
                  style={{ marginRight: "3px" }}
                />
              }
              text={`${currentMemberData?.totalSubscribers ?? 0} ${SingularPlural(
                currentMemberData?.totalSubscribers ?? 0,
                "Subscriber"
              )}`}
              width="fit-content"
              style={{
                fontSize: "1rem",
                color: "var(--color-gold)", // Match your styles
              }}
            />
          </HashLink>
          {verifyNeeded && (
            <Television
              shouldTvText={false}
              onCancelled={() => {
                setVerifyNeeded(false); // hide the tv
                setCurrentContextType(FORM_FLOW_CONTROL.SIGN_IN_FORM); // baseline
                setCurrentFormStep(FORM_FLOW_CONTROL.SIGN_IN_FORM);
              }}
              onVerified={onVerified} // optional: hide after success
            />
          )}
          {accountHolder &&
            !verifyNeeded && ( // used to show the correct form from the auth form component
              <AcctHolderUpdateModal
                userId={userId}
                show={editMode} // Use the existing `editMode` to control modal visibility that is wrapping the update profile form
                onClose={() => {
                  setEditMode(false); // Close modal by updating editMode
                  // optional baseline:
                  setCurrentContextType(FORM_FLOW_CONTROL.SIGN_IN_FORM);
                }}
                currentMemberData={currentMemberData}
                setCurrentMemberData={setCurrentMemberData}
                thumbnailPreference={thumbnailPreference}
                setThumbnailPreference={setThumbnailPreference}
                currentMemberId={currentMemberId}
                setEditMode={setEditMode}
                setLoading={setLoading} // Using the actual setLoading function
              >
                <div className="modalContent">
                  {currentMemberData && (
                    <p className="username">
                      Username: {currentMemberData.username}
                    </p>
                  )}
                  {/* Add the rest of your modal content and buttons here */}
                </div>
              </AcctHolderUpdateModal>
            )}
          {loading && <Loader />}
          {reportingSkit && (
            <ReportModal
              onBlowWhistle={reportSkit}
              kind="skit"
              onCancel={() => setReportingSkit(null)}
            />
          )}
          <div className="skit-list-wrapper">
            <div id="skitList">
              {noSkitsAvailable ? (
                <div className="no-skits-message">
                  Skits coming soon to a device near you...
                </div>
              ) : (
                skitsData
                  .filter((skit) => !notInterested.includes(skit.id))
                  .map((skit) => (
                    <SkitAdCard
                      key={skit.id}
                      specificSkitData={skit}
                      onBlowWhistle={() => setReportingSkit(skit)}
                      currentMemberId={currentMemberId}
                      onDelete={
                        () => handleDeleteSourceIdRelatedIdSkit(null, skit.id) // Corrected line
                      } // Passing skit.id to the delete handler
                      onNotInterested={() => handleNotInterested(skit)}
                      isUploadVideoFormOpen={openSkitUploadForm} // Pass the prop here
                    />
                  ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
export default UserProfilePage;
